import type { DataSourceRequestState, SortDescriptor } from "@progress/kendo-data-query";
import { DropDownList, type DropDownListChangeEvent } from "@progress/kendo-react-dropdowns";
import {
    GridColumn,
    GridNoRecords,
    type GridCellProps,
    type GridDataStateChangeEvent,
    type GridPageChangeEvent,
    type GridSortChangeEvent
} from "@progress/kendo-react-grid";
import { skipToken } from "@reduxjs/toolkit/query";
import { useState } from "react";
import { useListCustomersQuery } from "../../api/admin/adminCustomerApi";
import { useListProductsQuery } from "../../api/admin/adminProductApi";
import { useListAdminRetailersQuery } from "../../api/admin/adminRetailerApi";
import AppGrid from "../../components/grid/appGrid";
import { PlanningPeriod, UnitOfMeasure, type CustomerDto, type ProductDto, type RetailerDto } from "../../contracts";

const initialSort: SortDescriptor[] = [{ field: "name", dir: "asc" }];

const customerRequest: DataSourceRequestState = {
    skip: 0,
    take: 1000,
    sort: initialSort
};

const buildProductRequest = (
    dataState: DataSourceRequestState,
    customer: CustomerDto | null,
    retailer: RetailerDto | null
): DataSourceRequestState | typeof skipToken => {
    if (customer == null) {
        return skipToken;
    }

    return {
        ...dataState,
        filter: {
            logic: "and",
            filters: [
                ...(dataState.filter == null ? [] : [dataState.filter]),
                {
                    field: "retailer.customerId",
                    operator: "eq",
                    value: customer.id
                },
                ...(retailer == null
                    ? []
                    : [
                            {
                                field: "retailerId",
                                operator: "eq",
                                value: retailer.id
                            }
                        ])
            ]
        }
    };
};

const unitLabels: Record<UnitOfMeasure, string> = {
    [UnitOfMeasure.Each]: "each",
    [UnitOfMeasure.Gram]: "g",
    [UnitOfMeasure.Kilogram]: "kg",
    [UnitOfMeasure.Millilitre]: "ml",
    [UnitOfMeasure.Litre]: "L"
};

const planningPeriodLabels: Record<PlanningPeriod, string> = {
    [PlanningPeriod.Daily]: "Daily",
    [PlanningPeriod.Weekly]: "Weekly",
    [PlanningPeriod.Monthly]: "Monthly",
    [PlanningPeriod.Quarterly]: "Quarterly"
};

const ProductDataCell = (props: GridCellProps): React.ReactElement => {
    const product = props.dataItem as ProductDto;
    const unit = product.unit ?? UnitOfMeasure.Each;
    const unitsPerPack = product.units_per_pack != null && product.units_per_pack > 0
        ? product.units_per_pack
        : 1;
    const unitSize = product.unit_size != null && product.unit_size > 0
        ? product.unit_size
        : null;

    const unitDescription = unit === UnitOfMeasure.Each
        ? unitsPerPack === 1
            ? "Single item"
            : `${unitsPerPack} items per pack`
        : unitSize == null
            ? `${unitsPerPack} ${unitsPerPack === 1 ? "unit" : "units"} per pack`
            : `${unitsPerPack} X ${unitSize} ${unitLabels[unit]}`;

    return (
        <td className="k-table-td">
            <div className="fw-semibold">{product.name}</div>
            <div className="small text-muted">{unitDescription}</div>
        </td>
    );
};

const PlanningPeriodDataCell = (props: GridCellProps): React.ReactElement => {
    const product = props.dataItem as ProductDto;

    return (
        <td className="k-table-td">
            {planningPeriodLabels[product.planning_period]}
        </td>
    );
};

const ProductManagement: React.FC = () => {
    const [selectedCustomer, setSelectedCustomer] = useState<CustomerDto | null>(null);
    const [selectedRetailer, setSelectedRetailer] = useState<RetailerDto | null>(null);
    const [dataState, setDataState] = useState<DataSourceRequestState>({
        skip: 0,
        take: 50,
        sort: initialSort
    });

    const productRequest = buildProductRequest(dataState, selectedCustomer, selectedRetailer);

    const retailerRequest: DataSourceRequestState | typeof skipToken = selectedCustomer == null
        ? skipToken
        : {
                skip: 0,
                take: 1000,
                sort: initialSort,
                filter: {
                    logic: "and",
                    filters: [
                        {
                            field: "customerId",
                            operator: "eq",
                            value: selectedCustomer.id
                        }
                    ]
                }
            };

    const {
        data,
        isFetching,
        isError
    } = useListProductsQuery(productRequest);

    const {
        data: customerData,
        isFetching: isCustomersFetching,
        isError: isCustomersError
    } = useListCustomersQuery(customerRequest);

    const {
        currentData: retailerData,
        isFetching: isRetailersFetching,
        isError: isRetailersError
    } = useListAdminRetailersQuery(retailerRequest);

    const resetPaging = (): void => {
        setDataState(previous => ({
            ...previous,
            skip: 0
        }));
    };

    const handleCustomerChange = (event: DropDownListChangeEvent): void => {
        const customer = event.value as CustomerDto | null;

        setSelectedCustomer(customer?.id === 0 ? null : customer);
        setSelectedRetailer(null);
        resetPaging();
    };

    const handleRetailerChange = (event: DropDownListChangeEvent): void => {
        const retailer = event.value as RetailerDto | null;

        setSelectedRetailer(retailer?.id === 0 ? null : retailer);
        resetPaging();
    };

    const pageChange = (event: GridPageChangeEvent): void => {
        setDataState(previous => ({
            ...previous,
            skip: event.page.skip,
            take: event.page.take
        }));
    };

    const dataStateChange = (event: GridDataStateChangeEvent): void => {
        setDataState({
            ...event.dataState,
            skip: event.dataState.skip,
            take: event.dataState.take
        });
    };

    const sortChange = (event: GridSortChangeEvent): void => {
        setDataState(previous => ({
            ...previous,
            sort: event.sort,
            skip: 0
        }));
    };

    const emptyMessage = selectedCustomer == null
        ? "Select a customer to view products."
        : isError
            ? "Unable to load products. Please try again."
            : "No products found for the selected filters.";

    return (
        <div className="d-flex flex-column gap-3 p-3 h-100 min-h-0 overflow-hidden">
            <div className="row g-3 align-items-end">
                <div className="col-12 col-md-4">
                    <label
                        className="form-label fw-semibold"
                        htmlFor="admin-product-customer"
                    >
                        Customer
                    </label>

                    <DropDownList
                        id="admin-product-customer"
                        data={customerData?.data ?? []}
                        textField="name"
                        dataItemKey="id"
                        value={selectedCustomer}
                        onChange={handleCustomerChange}
                        disabled={isCustomersFetching}
                        defaultItem={{
                            id: 0,
                            name: isCustomersFetching
                                ? "Loading customers..."
                                : "Select customer"
                        }}
                    />
                </div>

                <div className="col-12 col-md-4">
                    <label
                        className="form-label fw-semibold"
                        htmlFor="admin-product-retailer"
                    >
                        Retailer
                    </label>

                    <DropDownList
                        id="admin-product-retailer"
                        data={retailerData?.data ?? []}
                        textField="name"
                        dataItemKey="id"
                        value={selectedRetailer}
                        onChange={handleRetailerChange}
                        disabled={selectedCustomer == null || isRetailersFetching}
                        defaultItem={{
                            id: 0,
                            name: isRetailersFetching
                                ? "Loading retailers..."
                                : "All retailers"
                        }}
                    />
                </div>
            </div>

            {(isError || isCustomersError || isRetailersError) && (
                <div className="alert alert-danger mb-0" role="alert">
                    Unable to load products, retailers, or customers. Please try again.
                </div>
            )}

            <div className="flex-grow-1 min-h-0 overflow-hidden">
                <AppGrid
                    scrollable="scrollable"
                    showLoader={isFetching || isCustomersFetching || isRetailersFetching}
                    dataItemKey="id"
                    sortable={false}
                    data={data?.data}
                    resizable={true}
                    initialState={dataState}
                    onPageChange={pageChange}
                    onDataStateChange={dataStateChange}
                    sort={dataState.sort}
                    total={data?.total ?? 0}
                    skip={dataState.skip}
                    take={dataState.take}
                    defaultSort={initialSort}
                    onSortChange={sortChange}
                    pageable={{ buttonCount: 3, pageSizes: [50, 100, 200] }}
                    style={{ height: "100%" }}
                >
                    <GridNoRecords>
                        {!isFetching && (
                            <span className="text-muted">
                                {emptyMessage}
                            </span>
                        )}
                    </GridNoRecords>

                    <GridColumn field="id" title="ID" width="40px" />
                    <GridColumn
                        field="name"
                        title="Product"
                        width="320px"
                        cells={{ data: ProductDataCell }}
                    />
                    <GridColumn field="sku" title="SKU" width="340px" />
                    <GridColumn field="barcode" title="Barcode" />
                    <GridColumn field="brand" title="Brand" />
                    <GridColumn field="sub_brand" title="Sub-brand" />
                    <GridColumn field="pack_size_key" title="Pack-size-key" />
                    <GridColumn field="base_price" title="Base Price" format="{0:c}" />
                    <GridColumn field="cost_price" title="Cost Price" format="{0:c}" />
                    <GridColumn field="inferred_average_price" title="Inferred Average Price" format="{0:c}" />
                    <GridColumn
                        field="planning_period"
                        title="Planning Period"
                        cells={{ data: PlanningPeriodDataCell }}
                    />
                </AppGrid>
            </div>
        </div>
    );
};

export default ProductManagement;
